import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
    Box,
    Paper,
    Stack,
    Typography,
    Divider,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Grid, // ✅ add this
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

const API_URL = "https://api.hr-ebnhaian.cloud/api/v1/centers";
const SERVER_ORIGIN = "https://api.hr-ebnhaian.cloud";

function fmtDate(d) {
    if (!d) return "-";
    try {
        return String(d).slice(0, 10);
    } catch {
        return "-";
    }
}

function daysLeft(endDate) {
    if (!endDate) return null;
    const end = new Date(endDate);
    if (Number.isNaN(end.getTime())) return null;
    const now = new Date();
    const diff = Math.floor((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
}

function ContractBlock({ title, data }) {
    const fileUrl = useMemo(() => {
        const url = data?.file;
        if (!url) return "";

        return url.startsWith("http")
            ? url
            : `${SERVER_ORIGIN}/${url.replace(/^\/+/, "")}`;
    }, [data]);

    const originalName = data?.file
        ? data.file.split("/").pop()
        : "";

    const end = data?.endDate || data?.expiryDate;
    const start = data?.startDate || data?.issueDate;

    const left = daysLeft(end);
    const isExpired = typeof left === "number" && left < 0;
    const isSoon = typeof left === "number" && left <= 30;

    return (
        <Paper sx={{ p: 2.2, borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontWeight: 900 }}>{title}</Typography>

                {end ? (
                    <Chip
                        size="small"
                        icon={isExpired || isSoon ? <NotificationsActiveIcon /> : undefined}
                        label={
                            isExpired
                                ? `Expired`
                                : isSoon
                                    ? `Ends in ${left} day(s)`
                                    : "OK"
                        }
                        color={isExpired ? "error" : isSoon ? "warning" : "success"}
                    />
                ) : (
                    <Chip size="small" label="No dates" />
                )}
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Stack spacing={0.8}>
                {"authority" in (data || {}) && (
                    <Typography sx={{ opacity: 0.8, fontSize: 13 }}>
                        Authority: {data?.authority || "-"}
                    </Typography>
                )}

                {"licenseNumber" in (data || {}) && (
                    <Typography sx={{ opacity: 0.8, fontSize: 13 }}>
                        License No: {data?.licenseNumber || "-"}
                    </Typography>
                )}

                <Typography sx={{ opacity: 0.8, fontSize: 13 }}>
                    Start: {fmtDate(start)}
                </Typography>

                <Typography sx={{ opacity: 0.8, fontSize: 13 }}>
                    End: {fmtDate(end)}
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Typography sx={{ fontWeight: 800, fontSize: 13 }}>File:</Typography>

                {!fileUrl ? (
                    <Typography sx={{ opacity: 0.7, fontSize: 13 }}>
                        No file uploaded
                    </Typography>
                ) : (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ opacity: 0.8, fontSize: 13 }}>
                            {originalName || "Contract File"}
                        </Typography>

                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<OpenInNewIcon />}
                            onClick={() => window.open(fileUrl, "_blank")}
                            sx={{ borderRadius: 3 }}
                        >
                            Open
                        </Button>

                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => navigator.clipboard.writeText(fileUrl)}
                            sx={{ borderRadius: 3 }}
                        >
                            Copy link
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
}
export default function CenterDetails() {
    const { id } = useParams();
    const nav = useNavigate();
    const [center, setCenter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const fetchOne = async () => {
        try {
            setErr("");
            setLoading(true);

            const res = await axios.get(`${API_URL}/${id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });

            setCenter(res.data?.data || null);
        } catch (e) {
            setErr(e?.response?.data?.message || e.message || "Failed to load center");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOne();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const statusColor = center?.status === "ACTIVE" ? "success" : "default";

    if (loading) {
        return (
            <Box p={3}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={22} />
                    <Typography>Loading...</Typography>
                </Stack>
            </Box>
        );
    }

    if (err) {
        return (
            <Box p={3}>
                <Alert severity="error" sx={{ borderRadius: 3 }}>
                    {err}
                </Alert>
                <Button sx={{ mt: 2 }} variant="outlined" onClick={() => nav(-1)}>
                    Back
                </Button>
            </Box>
        );
    }

    if (!center) {
        return (
            <Box p={3}>
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                    Center not found
                </Alert>
                <Button sx={{ mt: 2 }} variant="outlined" onClick={() => nav(-1)}>
                    Back
                </Button>
            </Box>
        );
    }

    return (
        <Box p={3} sx={{ background: "#f4f6fa", minHeight: "100vh" }}>
            <Box height={80} />
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => nav(-1)}
                    sx={{ borderRadius: 3 }}
                >
                    Back
                </Button>

                <Stack direction="row" spacing={1}>
                    <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => nav(`/centers/${id}/edit`)}
                        sx={{ borderRadius: 3 }}
                    >
                        Edit
                    </Button>
                </Stack>
            </Stack>

            <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
                    <Box>
                        <Typography variant="h5" fontWeight={900}>
                            {center.name}
                        </Typography>
                        <Typography sx={{ opacity: 0.7 }}>
                            Code: {center.code || "-"}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={center.status || "-"} color={statusColor} />
                        {center.mapLink ? (
                            <Button
                                variant="outlined"
                                sx={{ borderRadius: 3 }}
                                onClick={() => window.open(center.mapLink, "_blank")}
                            >
                                Open Map
                            </Button>
                        ) : null}
                    </Stack>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={0.8}>
                    <Typography><b>Phone:</b> {center.phone || "-"}</Typography>
                    <Typography><b>Email:</b> {center.email || "-"}</Typography>
                    <Typography><b>Address:</b> {center.address || "-"}</Typography>
                    {center.notes ? <Typography><b>Notes:</b> {center.notes}</Typography> : null}
                </Stack>
            </Paper>

            <Divider sx={{ my: 3, opacity: 0.6 }} />

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <ContractBlock title="Rental Contract" data={center.rentalContract} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <ContractBlock title="Fire Safety Contract" data={center.fireSafetyContract} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <ContractBlock title="Health License" data={center.healthLicense} />
                </Grid>
            </Grid>
        </Box>
    );
}
